import React, { useState } from "react";

interface CustomInputProps {
  name: string;
  label: string;
  type?: "text" | "textarea" | "date" | "time" | "password"; // Unterstützte Typen
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  required?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  customStyle?: React.CSSProperties; // Zusätzlicher CSS-Stil
}

const CustomInput: React.FC<CustomInputProps> = ({
  name,
  label,
  type = "text", // Standardmäßig "text"
  placeholder,
  value,
  defaultValue,
  required,
  onChange,
  onKeyDown,
  customStyle,
}) => {
  const [isFocused, setIsFocused] = useState(false); // Zustand für Fokus
  const [inputValue, setInputValue] = useState(defaultValue || value || "");

  // Handhabung der Eingabeänderung
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    if (onChange) onChange(e);
  };

  // Bedingung für das Label (immer oben, wenn ein Placeholder existiert)
  const isLabelFixed = placeholder || inputValue || isFocused;

  // Stile
  const inputContainerStyle: React.CSSProperties = {
    position: "relative",
    marginBottom: "45px",
    ...customStyle, // Zusätzlicher Stil
  };

  const labelStyle: React.CSSProperties = {
    color: isLabelFixed ? "#c39d63" : "rgb(52, 39, 20)",
    fontFamily: "Avenir Next Codensed Ultralight",
    fontSize: isLabelFixed ? "14px" : "1.6rem",
    fontWeight: 300,
    fontStyle: "normal",
    position: "absolute",
    pointerEvents: "none",
    left: "5px",
    top: isLabelFixed ? "-20px" : "1px",
    transition: "0.2s",
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: "Avenir Next Codensed Regular",
    color: "rgb(52, 39, 20)",
    background: "transparent",
    padding: type === "textarea" ? "10px" : "10px 10px 10px 5px",
    display: "block",
    width: "100%",
    border: "none",
    borderBottom: `1px solid ${isFocused ? "#c39d63" : "rgb(52, 39, 20)"}`,
    outline: "none",
    resize: type === "textarea" ? "vertical" : "none",
    transition: "border-bottom 0.2s ease",
  };

  const requiredStyle: React.CSSProperties = {
    color: "#BD2024",
    marginLeft: 6
  }

  return (
    <div style={inputContainerStyle}>
      {/* Label */}
      <label style={labelStyle}>{label}{required && (<span style={requiredStyle}>*</span>)}</label>
      {/* Input oder Textarea */}
      {type === "textarea" ? (
        <textarea
          id={name}
          name={name}
          style={inputStyle}
          placeholder={placeholder}
          value={value !== undefined ? value : inputValue}
          defaultValue={defaultValue}
          onChange={handleInputChange}
          onKeyDown={onKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          rows={4}
        />
      ) : (
        <input
          id={name}
          name={name}
          type={type}
          style={inputStyle}
          placeholder={placeholder}
          value={value !== undefined ? value : inputValue}
          defaultValue={defaultValue}
          onChange={handleInputChange}
          onKeyDown={onKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      )}
    </div>
  );
};

export default CustomInput;
